import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  WarningAmberRounded as WarningIcon,
  PowerSettingsNew as PowerIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';

const DEFAULT_MESSAGE = 'OOPS';

const DestroyPage: React.FC = () => {
  const navigate = useNavigate();
  const [message, setMessage] = React.useState(DEFAULT_MESSAGE);
  const [feedback, setFeedback] = React.useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const destroyToken = typeof window !== 'undefined' ? localStorage.getItem('destroy_token') : null;

  React.useEffect(() => {
    if (!destroyToken) {
      navigate('/login', { replace: true });
    }
  }, [destroyToken, navigate]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['maintenance-mode'],
    queryFn: () => apiService.getMaintenanceMode(),
    enabled: Boolean(destroyToken),
  });

  React.useEffect(() => {
    if (!data?.config) return;
    setMessage(data.config.message || DEFAULT_MESSAGE);
  }, [data?.config]);

  const mutation = useMutation({
    mutationFn: ({ enabled, customMessage }: { enabled: boolean; customMessage?: string }) =>
      apiService.updateMaintenanceMode(
        { enabled, message: customMessage ?? message },
        destroyToken ?? '',
      ),
    onSuccess: (res) => {
      setFeedback({
        kind: 'success',
        text: res.config.enabled
          ? 'Mode OOPS activé. Le front affiche désormais la page blanche.'
          : 'Mode OOPS désactivé. Le front est de nouveau accessible.',
      });
      void refetch();
    },
    onError: (err: any) => {
      setFeedback({
        kind: 'error',
        text: err?.response?.data?.error || 'Impossible de mettre à jour le mode maintenance.',
      });
    },
  });

  const handleLogout = () => {
    localStorage.removeItem('destroy_token');
    navigate('/login', { replace: true });
  };

  if (!destroyToken) {
    return null;
  }

  const enabled = data?.config?.enabled ?? false;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: enabled ? '#1c0000' : '#0f172a',
        color: '#fff',
        py: 6,
        px: 2,
        transition: 'background-color 0.3s ease',
      }}
    >
      <Box sx={{ maxWidth: 720, mx: 'auto' }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
          <WarningIcon sx={{ fontSize: 40, color: enabled ? '#f87171' : '#fbbf24' }} />
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: 0.5 }}>
            Destroy !
          </Typography>
        </Stack>

        <Typography variant="body1" sx={{ opacity: 0.85, mb: 3 }}>
          Bascule le front public Sherlook en page blanche « OOPS ». À n'utiliser
          qu'en cas d'urgence. Aucune trace de ce panneau n'est visible dans le
          menu standard du super-admin.
        </Typography>

        {feedback && (
          <Alert severity={feedback.kind} sx={{ mb: 3 }} onClose={() => setFeedback(null)}>
            {feedback.text}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Impossible de charger l'état actuel : {(error as Error).message}
          </Alert>
        )}

        <Card sx={{ bgcolor: '#1f2937', color: '#fff', mb: 3 }}>
          <CardContent>
            <Typography variant="overline" sx={{ color: '#94a3b8' }}>
              État actuel
            </Typography>
            {isLoading ? (
              <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={32} sx={{ color: '#fbbf24' }} />
              </Box>
            ) : (
              <>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    mt: 1,
                    mb: 0.5,
                    color: enabled ? '#f87171' : '#34d399',
                  }}
                >
                  {enabled ? 'MODE OOPS ACTIVÉ' : 'Mode normal'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#cbd5f5' }}>
                  Message public : <strong>{data?.config?.message || DEFAULT_MESSAGE}</strong>
                </Typography>
              </>
            )}
          </CardContent>
        </Card>

        <Card sx={{ bgcolor: '#1f2937', color: '#fff', mb: 3 }}>
          <CardContent>
            <Typography variant="overline" sx={{ color: '#94a3b8' }}>
              Message affiché
            </Typography>
            <TextField
              fullWidth
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="OOPS"
              variant="outlined"
              sx={{
                mt: 1,
                bgcolor: '#0f172a',
                borderRadius: 1,
                input: { color: '#fff' },
              }}
              inputProps={{ maxLength: 200 }}
              disabled={mutation.isPending}
            />
          </CardContent>
        </Card>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Button
            variant="contained"
            color={enabled ? 'inherit' : 'error'}
            size="large"
            startIcon={<PowerIcon />}
            disabled={mutation.isPending}
            onClick={() => mutation.mutate({ enabled: !enabled, customMessage: message })}
            sx={{
              flex: 1,
              py: 1.5,
              fontWeight: 800,
              fontSize: '1.05rem',
              textTransform: 'none',
              ...(enabled
                ? {
                    bgcolor: '#1f2937',
                    color: '#fff',
                    border: '1px solid #f87171',
                    '&:hover': { bgcolor: '#374151' },
                  }
                : {}),
            }}
          >
            {enabled ? 'Désactiver le mode OOPS' : 'Activer le mode OOPS'}
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<RestoreIcon />}
            onClick={handleLogout}
            sx={{ borderColor: '#fbbf24', color: '#fbbf24' }}
          >
            Se déconnecter
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default DestroyPage;
